'use client'

import { Gauge, Info } from 'lucide-react'
import { InfoPopover } from '@/components/patterns/infoPopover'
import { CardCanvas, CanvasPanel } from '@/components/patterns/nested-surface'
import { Badge } from '@/components/ui/badge'
import { CardDescription, CardTitle } from '@/components/ui/card'
import { wealthScoreBand } from '@/features/cases'
import { SegmentedGauge } from '@/features/investor/components/home/segmented-gauge'

/** Ported from `DashboardHome.tsx`'s `WealthScoreCard`. */
export function WealthScoreCard({ score }: { score: number }) {
  const band = wealthScoreBand(score)
  return (
    // Header rides inside the first panel rather than on the canvas, so every dashboard card reads
    // the same way: a warm canvas holding raised panels, nothing floating loose on the canvas itself.
    <CardCanvas>
      <CanvasPanel className="flex flex-1 flex-col gap-1 p-4">
        <div className="flex items-start gap-2.5">
          <span className="border-primary/20 bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-xl border">
            <Gauge className="size-4" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <CardTitle className="text-sm font-semibold">Wealth Score</CardTitle>
              <InfoPopover
                contentClassName="w-56 text-center text-sm"
                trigger={
                  <button
                    type="button"
                    aria-label="What does the Wealth Score mean?"
                    className="text-muted-foreground"
                  >
                    <Info className="size-3.5" />
                  </button>
                }
              >
                80+ Excellent · 60–79 Good · 40–59 Fair · below 40 needs attention.
              </InfoPopover>
            </div>
            <CardDescription className="text-xs">
              Holdings, diversification, and goals
            </CardDescription>
          </div>
        </div>

        <div className="relative flex flex-1 items-center justify-center">
          <div className="relative mx-auto w-full max-w-[280px]">
            <SegmentedGauge score={score} />
            <div className="absolute inset-x-0 top-[62%] flex -translate-y-1/2 flex-col items-center">
              <span className="text-foreground text-4xl font-semibold tabular-nums">{score}</span>
              <span className="text-muted-foreground text-sm">out of 100</span>
            </div>
          </div>
        </div>
      </CanvasPanel>

      <CanvasPanel className="flex flex-col items-center gap-1.5 px-3 py-2.5">
        <Badge className="border-transparent text-white" style={{ backgroundColor: band.color }}>
          {band.label}
        </Badge>
        <p className="text-muted-foreground max-w-56 text-center text-xs leading-snug">
          A balanced read of portfolio health and goal progress.
        </p>
      </CanvasPanel>
    </CardCanvas>
  )
}
